<template>
  <div
    :id="`${source}_add_phone_number`"
    class="flex flex-col h-full justify-between"
  >
    <PhoneNumberForm
      :source="source"
      :formData="phoneFormData"
      @update:formData="updateFormData"
      @validatePurchase="validatePurchase"
    />

    <VerificationStatus
      v-if="needsVerification"
      :source="source"
      :countryCode="phoneFormData.countryCode"
    />
  </div>

  <VerifyNumber
    source="phone_numbers"
    v-model:visible="showVerificationModal"
  />
</template>

<script setup>
import { ref, reactive, computed } from "vue";
import { message } from "ant-design-vue";
import { isPlivoProvider } from "@/src/utils/callsUtils.js";
import {
  countriesThatRequireVerification,
  countryMapping,
  countryTelephonyProviderMapping,
  telephonyProviderGenericWrapper,
} from "@/src/constants/phoneNumberConstants.js";
import store from "@/src/store/index.js";
import CallService from "@/src/api/CallService.js";
import VerifyNumber from "@/src/views/calls/phone_number/VerifyNumber.vue";
import PhoneNumberForm from "@/src/views/calls/phone_number/PhoneNumberForm.vue";
import VerificationStatus from "@/src/views/calls/phone_number/VerificationStatus.vue";

defineProps({
  source: { type: String, required: true },
});

const emit = defineEmits(["complete"]);

const purchasingNumber = ref(false);
const canPurchase = ref(false);
const showVerificationModal = ref(false);

const phoneFormData = reactive({
  assignedTo: "user",
  userId: null,
  teamId: null,
  countryCode: "US",
  areaCode: null,
  generatedNumberDetails: {
    number: "",
    friendly_name: "",
    area: "",
  },
});

const needsVerification = computed(() =>
  countriesThatRequireVerification.includes(phoneFormData.countryCode),
);

const isPhoneNumberVerified = computed(() => {
  return store.getters["workspace/isPhoneNumberVerified"];
});

const updateFormData = (newData) => {
  Object.assign(phoneFormData, newData);
};

const validatePurchase = () => {
  if (isPlivoProvider(phoneFormData.countryCode)) {
    canPurchase.value = !!(
      (phoneFormData.userId || phoneFormData.teamId) &&
      phoneFormData.generatedNumberDetails.number &&
      phoneFormData.countryCode
    );
  } else {
    canPurchase.value = !!(
      (phoneFormData.userId || phoneFormData.teamId) &&
      phoneFormData.generatedNumberDetails.number &&
      phoneFormData.countryCode &&
      (!needsVerification.value ? phoneFormData.areaCode : true)
    );
  }
};

const toPurchasePhoneNumber = async () => {
  try {
    if (
      countryMapping.US === phoneFormData.countryCode &&
      !isPhoneNumberVerified.value
    ) {
      showVerificationModal.value = true;
      return false;
    }

    purchasingNumber.value = true;

    const payload = {
      team_id: phoneFormData.teamId,
      user_id: phoneFormData.userId,
      phone_number: phoneFormData.generatedNumberDetails.number,
      area_code: phoneFormData.areaCode,
      country_code: phoneFormData.countryCode,
      provider:
        telephonyProviderGenericWrapper[
          countryTelephonyProviderMapping[phoneFormData.countryCode]
        ],
    };

    let bundleId = null;
    if (needsVerification.value) {
      const params = {
        country_code: phoneFormData.countryCode,
      };
      const response = await CallService.getBundleStatus(params);
      if (response?.success) {
        bundleId = response?.bundles_info?.[0]?.bundle_sid;

        if (bundleId) {
          payload.bundle_id = bundleId;
        }
      } else {
        console.error("Failed to fetch bundle status");
        purchasingNumber.value = false;
        return false;
      }
    }

    const response = await CallService.purchasePhoneNumber(payload);
    if (response?.success) {
      const data = response?.data;

      if (data?.sub_account_created) {
        await store.dispatch("dialerSettings/fetchDialerSettings");
      }

      message.success(data?.message);
      emit("complete", {
        phoneNumber: phoneFormData.generatedNumberDetails.number,
        subAccountCreated: data?.sub_account_created || false,
        provider:
          telephonyProviderGenericWrapper[
            countryTelephonyProviderMapping[phoneFormData.countryCode]
          ],
      });
      return true;
    }
    return false;
  } catch (error) {
    console.error("An error occurred while purchasing phone number: ", error);
    message.error(
      error?.response?.data?.error || "Failed to purchase phone number",
    );
    return false;
  } finally {
    purchasingNumber.value = false;
  }
};

const resetFormData = () => {
  phoneFormData.userId = null;
  phoneFormData.teamId = null;
  phoneFormData.areaCode = null;
  phoneFormData.countryCode = "US";
  phoneFormData.generatedNumberDetails = {
    number: "",
    friendly_name: "",
    area: "",
  };
  canPurchase.value = false;
};

defineExpose({
  toPurchasePhoneNumber,
  canPurchase,
  purchasingNumber,
  resetFormData,
});
</script>
